import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronRight, DollarSign, Hash, TrendingUp } from 'lucide-react';
import { Badge, Card, EmptyCircle, Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { PlaeeOrderResponseDto } from '@/lib/models';
import { fmtDateTime, fmtUsd, signedUsd } from '@/lib/trading/format';
import { formatEventDate, formatWindowRange, type FinishedEventGroup } from '@/lib/trading/portfolio-groups';

/**
 * History tab card for one closed EVENT (Figma 1869-61777) — the aggregate of
 * the user's finished positions in it, always fully shown (no expander):
 * category line + realized P&L chip and return %, question + close date,
 * "Your Pick" (joined answers), a Buy in / Exit / Quantity dollar box, the
 * Bought/Sold timestamps, and Go to Market. The per-position breakdown lives
 * on the market page's My History section.
 */
export function HistoryEventCard({ group, className }: { group: FinishedEventGroup; className?: string }) {
  const navigate = useNavigate();
  const gain = group.totalPnl >= 0;
  const finishedLabel = group.settled ? 'Settled' : 'Sold';
  const yourPick = group.picks.slice(0, 2).join(', ') + (group.picks.length > 2 ? ` +${group.picks.length - 2}` : '');

  return (
    <Card variant="default" className={cn('flex flex-col gap-3 p-3.5', className)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <EmptyCircle src={group.imageUrl} alt={group.title} size="sm" />
          <span className="truncate text-sm font-semibold text-purple">{group.category ?? 'Prediction'}</span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <Badge
            variant={gain ? 'success' : 'danger'}
            className={cn('rounded-pill px-2.5 py-0.5 text-sm font-semibold', gain ? 'bg-success/10' : 'bg-danger/10')}
          >
            {signedUsd(group.totalPnl)}
          </Badge>
          {group.pnlPct != null && (
            <span className={cn('text-xs font-semibold', gain ? 'text-success' : 'text-danger-alt')}>
              {group.pnlPct >= 0 ? '+' : ''}
              {group.pnlPct}%
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-0.5">
        <h3 className="text-base font-semibold text-primary leading-snug">{group.title}</h3>
        {group.closesAt && (
          <p className="text-sm font-light text-primary/80">
            {group.recurring ? formatWindowRange(group.opensAt, group.closesAt) : formatEventDate(group.closesAt)}
          </p>
        )}
        <div className="mt-1 flex items-center gap-2">
          <span className="text-sm font-semibold text-primary">Your Pick:</span>
          <span className="truncate text-sm font-semibold text-brand-grad-deep">{yourPick || '—'}</span>
        </div>
      </div>

      <div className="flex items-stretch justify-between gap-2 rounded-card bg-surface-muted/60 border border-border px-3.5 py-2.5">
        <HistoryStat
          icon={<DollarSign className="size-4 text-primary" aria-hidden />}
          label="Buy in"
          value={fmtUsd(group.totalCost)}
        />
        <div className="w-px bg-border self-stretch" aria-hidden />
        <HistoryStat
          icon={
            gain ? (
              <ArrowUp className="size-4 text-success" aria-hidden />
            ) : (
              <ArrowDown className="size-4 text-danger-alt" aria-hidden />
            )
          }
          label="Exit"
          value={fmtUsd(group.proceeds)}
        />
        <div className="w-px bg-border self-stretch" aria-hidden />
        <HistoryStat
          icon={<Hash className="size-4 text-primary" aria-hidden />}
          label="Quantity"
          value={String(group.totalQuantity || '—')}
        />
      </div>

      <p className="text-xs text-muted">
        Bought {fmtDateTime(group.openedAt)}
        {group.finishedAt ? ` · ${finishedLabel} ${fmtDateTime(group.finishedAt)}` : ''}
      </p>

      {(group.recurring ? group.instrument : group.eventId) && (
        <button
          type="button"
          onClick={() =>
            navigate(
              group.recurring
                ? `/snake/${group.instrument}${group.subCategoryCode ? `/${group.subCategoryCode}` : ''}`
                : `/events/${group.eventId}`,
            )
          }
          className="flex cursor-pointer items-center justify-center gap-1 self-end text-sm font-semibold text-purple transition-colors hover:text-brand-grad-deep"
        >
          <span>Go to Market</span>
          <ChevronRight className="size-4" aria-hidden />
        </button>
      )}
    </Card>
  );
}

/**
 * History tab card for a cancelled order: category line + Cancelled chip,
 * question + close date, "Your Pick", the cancel timestamp, the order's
 * Limit Price / Quantity / Total, and Go to Market. Never merged into an
 * event's position aggregate — cancelled orders carry no P&L.
 */
export function HistoryOrderCard({ order, className }: { order: PlaeeOrderResponseDto; className?: string }) {
  const navigate = useNavigate();
  const totalDollars = (order.price / 100) * order.orderQty;
  return (
    <Card variant="default" className={cn('flex flex-col gap-3 p-3.5', className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <EmptyCircle src={order.eventImageUrl ?? order.categoryIconUrl} alt={order.eventTitle ?? ''} size="sm" />
          <span className="truncate text-sm font-semibold text-purple">{order.category ?? 'Prediction'}</span>
        </div>
        <Badge
          variant="default"
          className="shrink-0 rounded-pill bg-surface-muted px-2.5 py-0.5 text-xs font-semibold normal-case text-muted"
        >
          {order.ordStatusString ?? 'Cancelled'}
        </Badge>
      </div>

      <div className="flex flex-col gap-0.5">
        <h3 className="text-base font-semibold text-primary leading-snug">
          {order.eventTitle ?? order.marketName ?? 'Market'}
        </h3>
        {order.closesAt && <p className="text-sm font-light text-primary/80">{formatEventDate(order.closesAt)}</p>}
        <div className="mt-1 flex items-center gap-2">
          <span className="text-sm font-semibold text-primary">Your Pick:</span>
          <span className="truncate text-sm font-semibold text-brand-grad-deep">
            {order.outcomeName ?? (order.contractSide === 'yes' ? 'Yes' : 'No')}
          </span>
        </div>
        <p className="text-xs text-muted">Cancelled {fmtDateTime(order.transactTime)}</p>
      </div>

      <div className="flex items-stretch justify-between gap-2 rounded-card bg-surface-muted/60 border border-border px-3.5 py-2.5">
        <HistoryStat
          icon={<TrendingUp className="size-4 text-primary" aria-hidden />}
          label="Limit Price"
          value={`${order.price}¢`}
        />
        <div className="w-px bg-border self-stretch" aria-hidden />
        <HistoryStat
          icon={<Hash className="size-4 text-primary" aria-hidden />}
          label="Quantity"
          value={String(order.orderQty)}
        />
        <div className="w-px bg-border self-stretch" aria-hidden />
        <HistoryStat
          icon={<DollarSign className="size-4 text-primary" aria-hidden />}
          label="Total"
          value={fmtUsd(totalDollars)}
        />
      </div>

      {order.eventId && (
        <button
          type="button"
          onClick={() => navigate(`/events/${order.eventId}`)}
          className="flex cursor-pointer items-center justify-center gap-1 self-end text-sm font-semibold text-purple transition-colors hover:text-brand-grad-deep"
        >
          <span>Go to Market</span>
          <ChevronRight className="size-4" aria-hidden />
        </button>
      )}
    </Card>
  );
}

/**
 * Loading placeholder mirroring a {@link HistoryEventCard}: category row +
 * P&L chip, title, date, pick line, the three-cell stats box, timestamps and
 * the Go to Market link.
 */
export function HistoryCardSkeleton() {
  return (
    <Card variant="default" className="flex flex-col gap-3 p-3.5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Skeleton className="size-[30px] shrink-0 rounded-full" />
          <Skeleton className="h-4 w-36 rounded" />
        </div>
        <div className="flex flex-col items-end gap-1">
          <Skeleton className="h-6 w-16 rounded-pill" />
          <Skeleton className="h-3 w-10 rounded" />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-5 w-3/4 rounded" />
        <Skeleton className="h-4 w-32 rounded" />
        <Skeleton className="mt-1 h-4 w-40 rounded" />
      </div>
      <div className="flex items-stretch gap-2 rounded-card border border-border bg-surface-muted/60 px-3.5 py-2.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-1 items-stretch gap-2">
            {i > 0 && <div className="w-px self-stretch bg-border" aria-hidden />}
            <div className="flex flex-1 flex-col gap-1.5">
              <Skeleton className="h-3 w-14 rounded" />
              <Skeleton className="h-5 w-12 rounded" />
            </div>
          </div>
        ))}
      </div>
      <Skeleton className="h-3 w-48 rounded" />
      <Skeleton className="h-4 w-24 self-end rounded" />
    </Card>
  );
}

function HistoryStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex flex-1 flex-col gap-0.5">
      <div className="flex items-center gap-1.5 text-muted">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <span className="text-base font-semibold text-primary">{value}</span>
    </div>
  );
}
